import axios from 'axios'
import { createAuthRequestInterceptor, createAuthResponseInterceptor } from '@/auth/api-auth-interceptors'
import { getAuthBridge } from '@/auth/auth-bridge'

// Atlas API client (ADR-0011). The access token is sourced from the
// provider-owned OIDC session through the auth bridge — never a duplicated
// `sessionStorage.accessToken` copy, never a decoded token, never Cognito claims.
// Tokens, authorization headers, and callback params are never logged.

// Finite request timeout. No canonical project-wide timeout constant exists, so we
// declare a clearly named local default: 30s bounds a hung request (it then fails
// via the error path instead of pending forever) while staying generous for slow
// Atlas API calls. Centralize here if a shared constant is later introduced.
export const API_REQUEST_TIMEOUT_MS = 30_000

export const apiClient = axios.create({
	baseURL: import.meta.env.VITE_API_URL || '/api',
	timeout: API_REQUEST_TIMEOUT_MS,
	headers: {
		'Content-Type': 'application/json',
	},
})

// Request: attach `Authorization: Bearer <access_token>` from the OIDC bridge only
// when a valid access token exists.
apiClient.interceptors.request.use(createAuthRequestInterceptor({ getBridge: getAuthBridge }), (error) =>
	Promise.reject(error),
)

// Response: the typed, loop-safe 401/403 auth state machine (single shared renewal +
// at most one replay, at most one login redirect, typed 403 states). No general axios
// retries here — only `GET /users/auth/me` keeps its bounded react-query retry.
apiClient.interceptors.response.use(
	(response) => response,
	createAuthResponseInterceptor({
		getBridge: getAuthBridge,
		replay: (config) => apiClient.request(config),
	}),
)
